import { Injectable } from '@angular/core';
import { RestService } from '../../../services/rest.service';
import { PaginatedResponse } from '../../../interface/PaginatedResponse';
import { MasterPayment } from '../interface/master-payment.interface';
import { PaymentStatus } from '../enum/payment-status.enum';

export interface MasterPaymentQuery {
  userId?: string;
  status?: PaymentStatus | string;
}

interface MasterPaymentsApiResponse {
  payments: MasterPayment[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Master payment ledger (`GET /master/payments`). Separate from order payment fields.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentMasterApiService {
  static readonly ApiUrl = 'master/payments';

  constructor(private restService: RestService) {}

  async readAll(
    page: number = 1,
    limit: number = 10,
    filters: MasterPaymentQuery = {},
  ): Promise<PaginatedResponse<MasterPayment>> {
    const data = (
      await this.restService.send<MasterPaymentsApiResponse>({
        url: PaymentMasterApiService.ApiUrl,
        method: 'GET',
        query: {
          page,
          limit,
          userId: filters.userId || undefined,
          status: filters.status || undefined,
        },
      })
    ).data;

    const size = data.limit || limit;
    const totalDocs = data.total ?? 0;
    const currentPage = data.page ?? page;
    const totalPages = size > 0 ? Math.max(1, Math.ceil(totalDocs / size)) : 1;

    return {
      docs: data.payments || [],
      totalDocs,
      page: currentPage,
      totalPages,
      size,
      hasNext: currentPage < totalPages,
      hasPrevious: currentPage > 1,
    };
  }

  async readOne(paymentId: string): Promise<MasterPayment> {
    return (
      await this.restService.send<MasterPayment>({
        url: `${PaymentMasterApiService.ApiUrl}/${paymentId}`,
        method: 'GET',
      })
    ).data;
  }
}
